import React, { useState, useContext, useEffect } from 'react';
import { useGetPostesQuery, useAssignPosteToUserMutation } from '../../services/SkillsManagement';
import { MenuItem, ListItemIcon, ListItemText, Box, FormControl, InputLabel, Select } from '@mui/material';
import WorkIcon from '@mui/icons-material/Work';
import { SnackBarContext } from '../../Context/SnackBarContextProvider';
import MuiDialog from '../../SystemDesign/MuiDialog';
import CircularProgress from '@mui/material/CircularProgress';
import { useDispatch } from 'react-redux';
import { ASSIGN_POSTE_TO_EMPLOYEE, INITIALIZE_POSTES } from '../../Reducers/Actions';

const AssignPositionToEmployee = ({ row }) => {
  // State management
  const [selectedPoste, setSelectedPoste] = useState('');
  const [close, setClose] = useState(false);
  const dispatch = useDispatch();

  // Context for notifications
  const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);

  // Get all postes from backend (keeping API field names intact)
  const { data: rawPostesData, isLoading: postesLoading } = useGetPostesQuery(
    {
      filters: "",
      getAllWithoutPagination: true,
      sorting: "",
    },
    { refetchOnMountOrArgChange: true }
  );

  // Transform postes into dropdown data
  const postesData =
    rawPostesData?.postes?.map((poste) => ({
      id: poste.id || poste.posteId,
      title: poste.name,
    })) || [];

  useEffect(() => {
    if (rawPostesData?.postes) {
      dispatch({ type: INITIALIZE_POSTES, payload: rawPostesData.postes });
    }
  }, [rawPostesData?.postes, dispatch]);

  // Initialize selected poste
  useEffect(() => {
    if (row.original.posteId) {
      setSelectedPoste(row.original.posteId);
    }
  }, [row.original.posteId]);

  // Mutation for assigning poste
  const [assignPoste] = useAssignPosteToUserMutation();

  // Handle position selection
  const handlePosteChange = (event) => {
    setSelectedPoste(event.target.value);
  };

  // Handle save position assignment
  const handleSubmit = async () => {
    if (!selectedPoste) {
      setInfoAlert({
        severity: 'error',
        message: "Please select a position to assign",
      });
      setOpenSnackBar(true);
      return;
    }

    try {
      const result = await assignPoste({
        posteId: Number(selectedPoste),
        userId: row.original.id,
      }).unwrap();

      // Update Redux store
      dispatch({
        type: ASSIGN_POSTE_TO_EMPLOYEE,
        payload: {
          userId: row.original.id,
          poste: result.user.poste || result.poste,
        },
      });

      setInfoAlert({
        severity: 'success',
        message: `Position successfully assigned to ${row.original.username}`,
      });
      setOpenSnackBar(true);
      return true;
    } catch (error) {
      console.error("Error assigning position:", error);
      setInfoAlert({
        severity: 'error',
        message: error.data?.message || "Error assigning position to user",
      });
      setOpenSnackBar(true);
      return false;
    }
  };

  return (
    <Box>
      <MuiDialog
        icon={<WorkIcon />}
        triggerButton={
          <MenuItem>
            <ListItemIcon>
              <WorkIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Assign Position</ListItemText>
          </MenuItem>
        }
        title="Assign Position to Employee"
        subtitle={`Assigning position to ${row.original.username}`}
        content={
          <Box sx={{ mt: 2, minWidth: '100%' }}>
            {postesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <CircularProgress />
              </Box>
            ) : (
              <FormControl fullWidth required>
                <InputLabel id="poste-select-label">Select Position</InputLabel>
                <Select
                  labelId="poste-select-label"
                  id="poste-select"
                  value={selectedPoste}
                  label="Select Position"
                  onChange={handlePosteChange}
                >
                  {postesData.map((poste) => (
                    <MenuItem key={poste.id} value={poste.id}>
                      {poste.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        }
        existAction={true}
        actionTitle="Assign"
        actionFunction={handleSubmit}
        onClose={close}
      />
    </Box>
  );
};

export default AssignPositionToEmployee;